import type { Accept, FileRejection } from 'react-dropzone'

// Formats de fichier acceptés par une zone de dépôt : PDF seul par défaut (justificatifs
// classiques), PDF / JPEG / PNG pour les pièces vérifiées par Netheos sur l'écran Justificatifs
// (miroir de `formats` renvoyé par `GET /justificatifs`).
export type FileFormat = 'pdf' | 'jpeg' | 'png'

const MAX_FILE_SIZE_MB = 20
export const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024

const FORMAT_ACCEPT: Record<FileFormat, Accept> = {
  pdf: { 'application/pdf': ['.pdf'] },
  jpeg: { 'image/jpeg': ['.jpg', '.jpeg'] },
  png: { 'image/png': ['.png'] },
}
const FORMAT_LABELS: Record<FileFormat, string> = { pdf: 'PDF', jpeg: 'JPEG', png: 'PNG' }

const DEFAULT_FORMATS: FileFormat[] = ['pdf']
export const PDF_ACCEPT: Accept = FORMAT_ACCEPT.pdf

export const buildAccept = (formats: FileFormat[]): Accept => (
  formats.reduce<Accept>((accept, format) => ({ ...accept, ...FORMAT_ACCEPT[format] }), {})
)

// « PDF » / « PDF, JPEG ou PNG »
export const formatsLabel = (formats: FileFormat[]): string => {
  const labels = formats.map((format) => FORMAT_LABELS[format])
  if (labels.length <= 1) return labels.join('')
  return `${labels.slice(0, -1).join(', ')} ou ${labels[labels.length - 1]}`
}

const extensionsOf = (formats: FileFormat[]) => (
  formats.flatMap((format) => Object.values(FORMAT_ACCEPT[format]).flat())
)

const invalidTypeMessage = (formats: FileFormat[]) => (
  formats.length === 1 && formats[0] === 'pdf'
    ? 'Format pdf uniquement'
    : `Formats acceptés : ${formatsLabel(formats)}`
)

const fileTooLargeMessage = (maxSizeMb: number) => `Ce fichier est trop volumineux (${maxSizeMb}mo maximum).`

// Mêmes contrôles et messages que `@components/Dropzone` (format vérifié côté serveur sur les
// octets ; limite multer 20 Mo).
export const checkFile = (
  file: File,
  formats: FileFormat[] = DEFAULT_FORMATS,
  maxSizeMb = MAX_FILE_SIZE_MB,
): string | null => {
  const name = file.name.toLowerCase()
  const hasAllowedExtension = extensionsOf(formats).some((extension) => name.endsWith(extension))
  if (!hasAllowedExtension) return invalidTypeMessage(formats)
  if (file.size >= maxSizeMb * 1024 * 1024) return fileTooLargeMessage(maxSizeMb)
  return null
}

export const checkPdfFile = (file: File, maxSizeMb = MAX_FILE_SIZE_MB): string | null => (
  checkFile(file, DEFAULT_FORMATS, maxSizeMb)
)

type DroppedFileOutcome =
  | { kind: 'file', file: File }
  | { kind: 'error', message: string }

// Résultat d'un dépôt react-dropzone (`onDrop(acceptedFiles, fileRejections)`). Les rejets du hook
// (options `accept`/`maxSize`/`multiple`) sont traduits en premier ; `attr-accept` acceptant un
// fichier dès que le MIME *ou* l'extension correspond, et `maxSize` refusant en `>` seulement,
// `checkFile` reste la dernière garde sur le fichier accepté.
export const resolveDroppedFile = (
  acceptedFiles: readonly File[],
  fileRejections: readonly Pick<FileRejection, 'errors'>[],
  formats: FileFormat[] = DEFAULT_FORMATS,
): DroppedFileOutcome => {
  const hasCode = (code: string) => fileRejections
    .some((rejection) => rejection.errors.some((error) => error.code === code))

  if (hasCode('too-many-files')) return { kind: 'error', message: 'Veuillez sélectionner un seul fichier' }
  if (hasCode('file-invalid-type')) return { kind: 'error', message: invalidTypeMessage(formats) }
  if (hasCode('file-too-large')) return { kind: 'error', message: fileTooLargeMessage(MAX_FILE_SIZE_MB) }

  const file = acceptedFiles[0]
  if (!file) return { kind: 'error', message: 'Aucun fichier reçu, veuillez réessayer.' }

  const error = checkFile(file, formats)
  return error ? { kind: 'error', message: error } : { kind: 'file', file }
}
